import { FormikHelpers } from "formik";
import { useNavigate } from "react-router-dom";
import { boronClient, makeFormData } from "../../../../../api";
import { UnprocessableEntityError } from "../../../../../errors";
import { useBoronMutation } from "../../../../../hooks/http/useBoronMutation";
import { useFlashMessage } from "../../../../../hooks/useFlashMessage";
import type { paths } from "../../../../../lib/api/v1";
import { setErrors } from "../../ImportOperation/useUploadForm";

export type StudentInactivationReason =
  paths["/api/v1/sections/{section_id}/import/student_passcode_inactivations"]["post"]["requestBody"]["content"]["multipart/form-data"]["reason"];

export type Values = {
  csv: Blob | undefined;
  reason: StudentInactivationReason;
};

const path =
  "/api/v1/sections/{section_id}/import/student_passcode_inactivations";

const handleInactivationError = (
  error: unknown,
  setFieldError?: FormikHelpers<Values>["setFieldError"],
  showErrorMessage?: (message: string) => void,
) => {
  if (!(error instanceof UnprocessableEntityError) || !setFieldError) {
    return showErrorMessage?.("一括操作が実行できませんでした");
  }

  const reasonError = error.errorTable?.reason;
  const reasonErrorPatterns = ["は一覧にありません", "を入力してください"];
  const hasReasonError = reasonErrorPatterns.some((pattern) =>
    reasonError.includes(pattern),
  );

  if (hasReasonError) {
    return setFieldError("reason", reasonError);
  }

  setErrors(setFieldError, error.originalErrors);
};

export const useImportStudentPasscodeInactivation = ({
  sectionId,
  setIsConfirmModalOpen,
}: {
  sectionId: string;
  setIsConfirmModalOpen: (value: boolean) => void;
}) => {
  const navigate = useNavigate();
  const { showErrorMessage } = useFlashMessage();

  const mutation = useBoronMutation(
    async ({ csv, reason }: { csv: Blob; reason: StudentInactivationReason }) =>
      await boronClient.POST(path, {
        params: { path: { section_id: sectionId } },
        body: { csv, reason },
        bodySerializer: makeFormData,
      }),
  );

  const importStudentPasscodeInactivation = (
    csvFile: Blob | null,
    reason: StudentInactivationReason,
    setFieldError?: FormikHelpers<Values>["setFieldError"],
  ) => {
    if (!csvFile) {
      throw new Error("CSVファイルが見つかりません");
    }

    mutation.mutate(
      { csv: csvFile, reason },
      {
        onSuccess: (data) => {
          const importHistory = data.importHistory;
          const redirectPath = `/sections/${sectionId}/settings/import_histories/${importHistory.id}`;
          navigate(redirectPath);
        },
        onError: (error) => {
          handleInactivationError(error, setFieldError, showErrorMessage);
        },
        onSettled: () => setIsConfirmModalOpen(false),
      },
    );
  };

  return {
    importStudentPasscodeInactivation,
  };
};
